import { useCallback, useMemo, useState } from "react";

/**
 * Which photos are picked. "Select all" reaches photos the screen has not loaded, so it is a
 * mode, not a list: `all` is true and `excluded` holds the ones taken back out. Otherwise
 * `picked` holds the ones chosen.
 */
interface State {
  all: boolean;
  picked: ReadonlySet<string>;
  excluded: ReadonlySet<string>;
  /** The last tile clicked, where a shift-click range starts. */
  anchor: string | null;
}

const EMPTY: State = { all: false, picked: new Set(), excluded: new Set(), anchor: null };

export interface PhotoSelection {
  count: number;
  all: boolean;
  excluded: string[];
  picked: string[];
  isSelected: (id: string) => boolean;
  /** Ctrl or cmd click: flip one photo. */
  toggle: (id: string) => void;
  /** Shift click: everything from the last click to this one, in the order shown. */
  range: (id: string, shown: readonly { id: string }[]) => void;
  selectAll: () => void;
  clear: () => void;
}

function withMember(set: ReadonlySet<string>, id: string, member: boolean): ReadonlySet<string> {
  const next = new Set(set);
  if (member) next.add(id);
  else next.delete(id);
  return next;
}

/** `total` is how many photos the current list holds on the server. */
export function usePhotoSelection(total: number): PhotoSelection {
  const [state, setState] = useState<State>(EMPTY);

  const setOne = useCallback((current: State, id: string, selected: boolean): State => {
    return current.all
      ? { ...current, excluded: withMember(current.excluded, id, !selected), anchor: id }
      : { ...current, picked: withMember(current.picked, id, selected), anchor: id };
  }, []);

  const isSelected = useCallback(
    (id: string) => (state.all ? !state.excluded.has(id) : state.picked.has(id)),
    [state],
  );

  const toggle = useCallback(
    (id: string) =>
      setState((current) => {
        const now = current.all ? !current.excluded.has(id) : current.picked.has(id);
        return setOne(current, id, !now);
      }),
    [setOne],
  );

  const range = useCallback(
    (id: string, shown: readonly { id: string }[]) =>
      setState((current) => {
        const to = shown.findIndex((photo) => photo.id === id);
        const from = current.anchor ? shown.findIndex((photo) => photo.id === current.anchor) : -1;
        if (to === -1 || from === -1) return setOne(current, id, true);
        const [start, end] = from < to ? [from, to] : [to, from];
        const picked = new Set(current.picked);
        const excluded = new Set(current.excluded);
        for (const photo of shown.slice(start, end + 1)) {
          if (current.all) excluded.delete(photo.id);
          else picked.add(photo.id);
        }
        return { ...current, picked, excluded, anchor: id };
      }),
    [setOne],
  );

  const selectAll = useCallback(() => setState({ ...EMPTY, all: true }), []);
  const clear = useCallback(() => setState(EMPTY), []);

  return useMemo(
    () => ({
      count: state.all ? Math.max(0, total - state.excluded.size) : state.picked.size,
      all: state.all,
      excluded: [...state.excluded],
      picked: [...state.picked],
      isSelected,
      toggle,
      range,
      selectAll,
      clear,
    }),
    [state, total, isSelected, toggle, range, selectAll, clear],
  );
}
